// Configuración · CLASIFICACIÓN como en Random (pedido de Rodrigo, 2026-10-13): un árbol súper
// familia › familia › subfamilia. Se abre/cierra cada rama, se busca, se crea una hija debajo de la
// elegida, se renombra y se desactiva. Lo hace quien tiene `catalogo.clasificacion.gestionar`;
// cada cambio queda auditado en el servidor.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { useSesion } from '../sesion/SesionContexto';
import { hijasDe, nombreNivel, useArbolCategorias, type CategoriaArbol } from '../precios/datos';

export function ArbolCategorias() {
  const { puede } = useSesion();
  const gestiona = puede('catalogo.clasificacion.gestionar');
  const q = useArbolCategorias();
  const qc = useQueryClient();
  const intentos = useIntentos();
  const cats = useMemo(() => q.data ?? [], [q.data]);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<CategoriaArbol | null>(null);
  const [buscar, setBuscar] = useState('');
  const [nueva, setNueva] = useState<{
    padre: CategoriaArbol | null;
    codigo: string;
    nombre: string;
  } | null>(null);
  const [nombre, setNombre] = useState('');

  const enviar = useMutation({
    mutationFn: (p: { metodo: 'POST' | 'PATCH'; ruta: string; cuerpo: unknown }) => {
      const idempotencia = intentos.clave(`cat:${p.metodo}:${p.ruta}`, p.cuerpo);
      return p.metodo === 'POST'
        ? api.post(p.ruta, p.cuerpo, { idempotencia })
        : api.patch(p.ruta, p.cuerpo, { idempotencia });
    },
    onSuccess: async () => {
      intentos.cerrar();
      setNueva(null);
      await qc.invalidateQueries({ queryKey: ['catalogo', 'categorias'] });
      await qc.invalidateQueries({ queryKey: ['config', 'categorias'] });
    },
    onError: (e) => intentos.cerrar(e),
  });

  // Con búsqueda: se muestran las que calzan y todos sus ancestros, abiertos.
  const porId = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const visibles = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    if (!t) return null;
    const v = new Set<string>();
    for (const c of cats)
      if (`${c.codigo} ${c.nombre}`.toLowerCase().includes(t)) {
        let x: CategoriaArbol | undefined = c;
        while (x) {
          v.add(x.id);
          x = x.padre_id ? porId.get(x.padre_id) : undefined;
        }
      }
    return v;
  }, [buscar, cats, porId]);

  const alternar = (id: string) =>
    setAbiertas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const elegir = (c: CategoriaArbol) => {
    setSel(c);
    setNombre(c.nombre);
    setNueva(null);
  };

  const rama = (padre: string | null, prof: number): React.ReactNode[] =>
    hijasDe(cats, padre)
      .filter((c) => !visibles || visibles.has(c.id))
      .flatMap((c) => {
        const hijas = cats.some((x) => x.padre_id === c.id);
        const abierta = !!visibles || abiertas.has(c.id);
        return [
          <li
            key={c.id}
            className={`nodo-arbol${sel?.id === c.id ? ' activo' : ''}${c.activa ? '' : ' inactiva'}`}
            style={{ paddingLeft: `${prof * 18 + 4}px` }}
          >
            <button
              type="button"
              className="flecha-arbol"
              aria-label={abierta ? `Cerrar ${c.nombre}` : `Abrir ${c.nombre}`}
              disabled={!hijas}
              onClick={() => alternar(c.id)}
            >
              {hijas ? (abierta ? '▾' : '▸') : '·'}
            </button>
            <button type="button" className="nombre-arbol" onClick={() => elegir(c)}>
              {c.nombre} <span className="apagado mono">{c.codigo}</span>
              {!c.activa && <span className="apagado"> · inactiva</span>}
            </button>
          </li>,
          ...(abierta ? rama(c.id, prof + 1) : []),
        ];
      });

  const actual = sel ? (porId.get(sel.id) ?? sel) : null;
  const sugerirCodigo = (padre: CategoriaArbol | null) => (padre ? `${padre.codigo}-` : '');
  return (
    <div className="pantalla arbol-categorias" data-pantalla="categorias">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar categoría"
            placeholder="nombre o código…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </label>
        <span className="apagado">
          {cats.filter((c) => c.nivel === 1).length} súper familias ·{' '}
          {cats.filter((c) => c.nivel === 2).length} familias ·{' '}
          {cats.filter((c) => c.nivel === 3).length} subfamilias
        </span>
        <span className="espaciador" />
        {gestiona && (
          <button type="button" onClick={() => setNueva({ padre: null, codigo: '', nombre: '' })}>
            Nueva súper familia
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {enviar.isError && <PanelError error={enviar.error} />}
      <div className="arbol-y-detalle">
        <ul className="arbol" aria-label="Árbol de categorías">
          {rama(null, 0)}
          {!cats.length && (
            <li className="apagado">{q.isLoading ? 'Cargando…' : 'Sin categorías'}</li>
          )}
        </ul>
        <aside className="detalle-arbol">
          {actual ? (
            <>
              <h3>
                {nombreNivel(actual.nivel)} · <span className="mono">{actual.codigo}</span>
              </h3>
              <p className="apagado">{[...caminoTexto(actual, porId)].join(' › ')}</p>
              <label className="campo">
                <span className="etiqueta">Nombre</span>
                <input
                  aria-label="Nombre de la categoría"
                  value={nombre}
                  disabled={!gestiona}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </label>
              {gestiona && (
                <div className="acciones-ficha">
                  <button
                    type="button"
                    className="primario"
                    disabled={
                      nombre.trim().length < 2 ||
                      nombre.trim() === actual.nombre ||
                      enviar.isPending
                    }
                    onClick={() =>
                      enviar.mutate({
                        metodo: 'PATCH',
                        ruta: `/catalogo/categorias/${actual.id}`,
                        cuerpo: { nombre: nombre.trim(), version: actual.version },
                      })
                    }
                  >
                    Renombrar
                  </button>
                  <button
                    type="button"
                    disabled={enviar.isPending}
                    onClick={() =>
                      enviar.mutate({
                        metodo: 'PATCH',
                        ruta: `/catalogo/categorias/${actual.id}`,
                        cuerpo: { activa: !actual.activa, version: actual.version },
                      })
                    }
                  >
                    {actual.activa ? 'Desactivar' : 'Activar'}
                  </button>
                  {actual.nivel < 3 && (
                    <button
                      type="button"
                      onClick={() =>
                        setNueva({ padre: actual, codigo: sugerirCodigo(actual), nombre: '' })
                      }
                    >
                      Agregar {nombreNivel(actual.nivel + 1).toLowerCase()}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="apagado">
              Elige una categoría para ver su camino, renombrarla o agregarle una{' '}
              {nombreNivel(2).toLowerCase()} o {nombreNivel(3).toLowerCase()}.
            </p>
          )}
          {nueva && (
            <div className="nueva-categoria">
              <h3>
                Nueva {nombreNivel((nueva.padre?.nivel ?? 0) + 1).toLowerCase()}
                {nueva.padre ? ` en ${nueva.padre.nombre}` : ''}
              </h3>
              <label className="campo">
                <span className="etiqueta">Código</span>
                <input
                  aria-label="Código de la nueva categoría"
                  value={nueva.codigo}
                  onChange={(e) => setNueva({ ...nueva, codigo: e.target.value.toUpperCase() })}
                />
              </label>
              <label className="campo">
                <span className="etiqueta">Nombre</span>
                <input
                  aria-label="Nombre de la nueva categoría"
                  value={nueva.nombre}
                  onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })}
                />
              </label>
              <div className="acciones-ficha">
                <button type="button" onClick={() => setNueva(null)}>
                  Volver
                </button>
                <button
                  type="button"
                  className="primario"
                  disabled={
                    !/^[A-Z0-9_-]{1,20}$/.test(nueva.codigo) ||
                    nueva.nombre.trim().length < 2 ||
                    enviar.isPending
                  }
                  onClick={() =>
                    enviar.mutate({
                      metodo: 'POST',
                      ruta: '/catalogo/categorias',
                      cuerpo: {
                        codigo: nueva.codigo,
                        nombre: nueva.nombre.trim(),
                        ...(nueva.padre ? { padreId: nueva.padre.id } : {}),
                      },
                    })
                  }
                >
                  Crear
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function caminoTexto(c: CategoriaArbol, porId: ReadonlyMap<string, CategoriaArbol>) {
  const r: string[] = [];
  let x: CategoriaArbol | undefined = c;
  while (x && r.length < 8) {
    r.unshift(x.nombre);
    x = x.padre_id ? porId.get(x.padre_id) : undefined;
  }
  return r;
}
